import api from './api';

export interface Round {
  _id: string;
  project_id: string;
  round_title: string;
  round_description?: string;
  round_order: number;
  questions: string[];
  general_info?: string;
  is_active: boolean;
  is_ongoing_round: boolean;
  is_streaming_round: boolean;
  start_date?: Date;
  end_date?: Date;
  timezone?: string;
  is_deleted: boolean;
  is_ai: boolean;
  screening_type?: string;
  created_by?: string;
  updated_by?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface RoundListResponse {
  success: boolean;
  count: number;
  pagination: {
    total: number;
    page: number;
    pages: number;
    limit: number;
  };
  data: Round[];
}

export interface RoundResponse {
  success: boolean;
  data: Round;
}

export interface RoundQueryParams {
  page?: number;
  limit?: number;
  is_active?: boolean;
  is_ongoing_round?: boolean;
  is_streaming_round?: boolean;
  search?: string;
}

class RoundService {
  /**
   * Get project rounds
   */
  async getProjectRounds(companyId: string, projectId: string, params: RoundQueryParams = {}): Promise<RoundListResponse> {
    try {
      const response = await api.get<RoundListResponse>(`/companies/${companyId}/projects/${projectId}/rounds`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching project rounds:', error);
      throw error;
    }
  }

  /**
   * Get round by ID
   */
  async getRound(companyId: string, projectId: string, roundId: string): Promise<Round> {
    try {
      const response = await api.get<RoundResponse>(`/companies/${companyId}/projects/${projectId}/rounds/${roundId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching round:', error);
      throw error;
    }
  }

  /**
   * Create round
   */
  async createRound(companyId: string, projectId: string, data: Partial<Round>): Promise<Round> {
    try {
      const response = await api.post<RoundResponse>(`/companies/${companyId}/projects/${projectId}/rounds`, data);
      return response.data.data;
    } catch (error) {
      console.error('Error creating round:', error);
      throw error;
    }
  }

  /**
   * Update round
   */
  async updateRound(companyId: string, projectId: string, roundId: string, data: Partial<Round>): Promise<Round> {
    try {
      const response = await api.put<RoundResponse>(`/companies/${companyId}/projects/${projectId}/rounds/${roundId}`, data);
      return response.data.data;
    } catch (error) {
      console.error('Error updating round:', error);
      throw error;
    }
  }

  /**
   * Update round status
   */
  async updateRoundStatus(companyId: string, projectId: string, roundId: string, statusData: {
    is_active?: boolean;
    is_ongoing_round?: boolean;
    is_streaming_round?: boolean;
  }): Promise<Round> {
    try {
      const response = await api.patch<RoundResponse>(
        `/companies/${companyId}/projects/${projectId}/rounds/${roundId}/status`, 
        statusData
      );
      return response.data.data;
    } catch (error) {
      console.error('Error updating round status:', error);
      throw error;
    }
  }

  /**
   * Delete round (soft delete)
   */
  async deleteRound(companyId: string, projectId: string, roundId: string): Promise<void> {
    try {
      await api.delete(`/companies/${companyId}/projects/${projectId}/rounds/${roundId}`);
    } catch (error) {
      console.error('Error deleting round:', error);
      throw error;
    }
  }

  /**
   * Reorder rounds
   */
  async reorderRounds(companyId: string, projectId: string, roundOrders: { id: string; round_order: number }[]): Promise<Round[]> {
    try {
      const response = await api.patch<RoundListResponse>(
        `/companies/${companyId}/projects/${projectId}/rounds/reorder`,
        { roundOrders }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error reordering rounds:', error);
      throw error;
    }
  }
}

export default new RoundService();